import { Button, Grid, makeStyles, Typography } from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import { useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import { AdviceStatuses, AmlStatuses, ClientDto, ClientStatuses, OnboardDelivery, Product } from "../../../api";
import { getClientDisplayName } from "../../../common/helpers";
import { useAppDispatch, useAppSelector } from "../../../store";
import { getClientAsync } from "../../../store/adviser";
import { AlertMessage, Colors, TitleText } from "../../controls/helpers";
import { applicationNeedsConfirmation, formatDate, getAllApplicants, getApplicationsByProduct, getJointClientApplications, sortApplicationClients } from "../Helpers";
import { ArchiveChip, CompletedChip, FailedChip, InProgressChip, NotStartedChip } from "../IconsAndChips";
import { Details, Section } from "../Section";
import { ApplicationAmlStatus, ConfirmationStatus, SatStatus } from "../StatusChips";
import AmlCheck from "./AmlCheck";
import Applicant from "./Applicant";
import ProductAccounts from "./ProductAccounts";

const JointAccountStatus = ({ client, title }: { client: ClientDto; title: string }) => {
  const jointAccounts = getJointClientApplications(client);
  if (jointAccounts.length == 0) return <></>;
  if (jointAccounts.every((p) => p.amlStatus!.split(", ").includes(AmlStatuses.Successful))) return <CompletedChip title={title} />;
  else if (jointAccounts.some((p) => p.amlStatus!.split(", ").includes(AmlStatuses.Failed))) return <FailedChip title={title} />;
  else if (jointAccounts.some((p) => p.clientStatus! == ClientStatuses.InProgress)) return <InProgressChip title={title} />;
  else return <NotStartedChip title={title} />;
};

export default ({ history, match }: RouteComponentProps) => {
  const clientId = (match.params as { clientId: string }).clientId;
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { client } = useAppSelector((state) => state.adviser);
  const wealthBuilderAccounts = getApplicationsByProduct({ ...client }, Product.WealthBuilder);
  const incomeGeneratorAccounts = getApplicationsByProduct({ ...client }, Product.IncomeGenerator);
  const kiwiSaverAccounts = getApplicationsByProduct({ ...client }, Product.KiwiSaver);
  const allApplicants = getAllApplicants({ ...client });
  const sortedApplicants = sortApplicationClients(allApplicants);
  const jointAccounts = getJointClientApplications(client);
  const completedJointApplicant = jointAccounts.find((p) => p.applicationCompleted);
  const isKsOnly = !wealthBuilderAccounts.length && !incomeGeneratorAccounts.length && !!kiwiSaverAccounts.length;
  let applicationDelivery = "";

  switch (client?.onboardDelivery) {
    case OnboardDelivery.PublicWebsite:
      applicationDelivery = "Public website";
      break;
    case OnboardDelivery.Browser:
    case OnboardDelivery.BrowserKiwiSaverOnly:
      applicationDelivery = "Browser";
      break;
    case OnboardDelivery.App:
    default:
      applicationDelivery = "App";
      break;
  }

  applicationDelivery += isKsOnly ? " - KS only" : "";

  useEffect(() => {
    dispatch(getClientAsync(clientId));
  }, [clientId]);

  return !client ? null : (
    <>
      <Grid container>
        {!!client.amlStatus?.split(", ").includes(AmlStatuses.Failed) && (
          <Grid item xs={12}>
            <AlertMessage error hideClose message="AML has not passed. Please see the 'AML checks' section below for more details." />
          </Grid>
        )}
        {applicationNeedsConfirmation(client) && (
          <Grid item xs={12} style={{ borderTop: !!client.amlStatus?.split(", ").includes(AmlStatuses.Failed) ? `1px solid ${Colors.White}` : "unset" }}>
            <AlertMessage
              error
              message="Advice confirmation is required"
              action={
                <Button variant="contained" color="primary" onClick={() => history.push(`/adviser/${clientId}/advice-confirmation`)}>
                  Go to advice confirmation
                </Button>
              }
            />
          </Grid>
        )}
      </Grid>
      <Grid container className={classes.container} style={{ backgroundColor: Colors.LightGrey }}>
        <Grid item md={6} xs={12} style={{ padding: "20px 20px 0" }}>
          <TitleText hideMarginBottom>
            <Grid container alignItems="center">
              <PersonIcon fontSize="large" htmlColor={Colors.Blue} />
              <Grid item>&nbsp;{getClientDisplayName(client)}</Grid>
            </Grid>
          </TitleText>
        </Grid>
        <Grid item md={6} xs={12} style={{ textAlign: "right", padding: "30px 18px 0 0" }}>
          {client.isArchived && <ArchiveChip title="Application archived" />}
        </Grid>
        <Grid item md={6} xs={12}>
          <Section hideBottomBorder hideTopBorder>
            <SatStatus adviceStatus={client.adviceStatus!} title="SAT" />
            <ApplicationAmlStatus
              title="Appl/AML"
              adviceStatus={client.adviceStatus!}
              clientStatus={client.clientStatus}
              amlStatus={client.amlStatus!}
              isRetention={client.kiwiSaverApplication?.isRetention}
            />
            <ConfirmationStatus client={client} title="Confirm" />
            <JointAccountStatus client={client} title="Joint" />
          </Section>
          <Section hideBottomBorder hideTopBorder>
            <Details title="Email">
              <Typography variant="subtitle1">
                <b>{client.email}</b>
              </Typography>
            </Details>
            {client.mobile && (
              <Details title="Mobile">
                <Typography variant="subtitle1">
                  <b>{client.mobile}</b>
                </Typography>
              </Details>
            )}
          </Section>
        </Grid>
        <Grid item md={6} xs={12}>
          <Section hideBottomBorder hideTopBorder>
            <Details title="Adviser">
              <Typography variant="subtitle1">
                <b>{client.adviserName}</b>
              </Typography>
            </Details>
            <Details title="Delivery">
              <Typography variant="subtitle1">
                <b>{applicationDelivery}</b>
              </Typography>
            </Details>
            {client.clientApplicationSavedDate && (
              <Details title="Saved">
                <Typography variant="subtitle1">
                  <b>{formatDate(client.clientApplicationSavedDate)}</b>
                </Typography>
              </Details>
            )}
            <Details title={client.adviceStatus === AdviceStatuses.Direct ? "Created" : "Sent"}>
              <Typography variant="subtitle1">
                <b>{formatDate(client.created)}</b>
              </Typography>
            </Details>
            {client.applicationCompleted && (
              <Details title={jointAccounts.length ? "Primary applicant completed" : "Completed"}>
                <Typography variant="subtitle1">
                  <b>{formatDate(client.applicationCompleted)}</b>
                </Typography>
              </Details>
            )}
            {completedJointApplicant && (
              <Details title="Secondary applicant completed">
                <Typography variant="subtitle1">
                  <b>{formatDate(completedJointApplicant?.applicationCompleted)}</b>
                </Typography>
              </Details>
            )}
            {!!client.kiwiSaverApplication?.agreementVersion && (
              <Details title="Advice agreement">
                <Typography variant="subtitle1">
                  <b>{`${client.kiwiSaverApplication.affiliation} ${client.kiwiSaverApplication.agreementVersion}`}</b>
                </Typography>
              </Details>
            )}
            {client.isArchived! && (
              <Details title="Archived">
                <Typography variant="subtitle1">
                  <b>{formatDate(client.archivedDate)}</b>
                </Typography>
              </Details>
            )}
          </Section>
        </Grid>
      </Grid>
      <Grid container className={classes.container} style={{ borderTop: `1px solid ${Colors.Grey}` }}>
        <Grid item sm={6} xs={12}>
          <Grid container>
            <Grid item xs={12} className={classes.sectionTitle}>
              <Typography variant="h5" gutterBottom>
                Accounts
              </Typography>
            </Grid>
            <ProductAccounts accounts={kiwiSaverAccounts} product={Product.KiwiSaver} isRetention={client.kiwiSaverApplication?.isRetention} />
            <ProductAccounts accounts={wealthBuilderAccounts} product={Product.WealthBuilder} />
            <ProductAccounts accounts={incomeGeneratorAccounts} product={Product.IncomeGenerator} />
          </Grid>
        </Grid>
        <Grid item sm={6} xs={12}>
          <Grid container>
            <Grid item xs={12} className={classes.sectionTitle}>
              <Typography variant="h5" gutterBottom>
                Applicants
              </Typography>
            </Grid>
            {sortedApplicants.map((c, i) => (
              <Applicant key={i} client={c} primaryClient={client} />
            ))}
            {client.applicationCompleted && !client.kiwiSaverApplication?.isRetention && (
              <>
                <Grid item xs={12} className={classes.sectionTitle}>
                  <Typography variant="h5" gutterBottom>
                    AML checks
                  </Typography>
                </Grid>
                {sortedApplicants.map((c, i) => (
                  <AmlCheck key={i} client={c} isKsOnly={isKsOnly} />
                ))}
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles(
  {
    container: {
      "@media(min-width: 768px)": {
        padding: "0 88px",
      },
    },
    sectionTitle: {
      padding: "20px 20px 0",
    },
  },
  { name: "ClientApplication" }
);
